/**
 * Employee merit winners.
 *
 * A phase can name tickets that are awarded on merit rather than by chance —
 * staff recognised for the campaign. This decides whether one of them is due on
 * the next spin.
 *
 * Pure and free of DOM or network, so the rules can be tested exhaustively.
 */

/**
 * How many prizes are drawn by chance before a merit winner is due.
 *
 * A third of the way in: late enough that a merit winner cannot come out of the
 * opening spin, early enough that a short draw still reaches them.
 */
export const meritThreshold = (maxWinners) => Math.ceil(maxWinners / 3);

/**
 * How many merit winners each gender still owes a prize to.
 *
 * The chance spins hold these slots back (see `pickGenderTrack`), because a
 * gender that fills its quota before its merit tickets come due locks them out
 * of the draw for good — the quota guard below would then refuse them, and the
 * remaining prizes all belong to the other gender.
 *
 * @returns {{M: number, F: number}}
 */
export const countPendingMeritWinners = ({meritWinners, eligiblePool}) => {
    if (!meritWinners?.length) return {M: 0, F: 0};

    const pending = new Set(meritWinners);
    const countIn = (list) => list.reduce((total, item) => total + (pending.has(item.lottery) ? 1 : 0), 0);

    return {M: countIn(eligiblePool.M), F: countIn(eligiblePool.F)};
};

/**
 * The employee merit winner the next spin must land on, if any.
 *
 * Resolved against the *eligible* pool, so a merit ticket that has already won —
 * or whose customer has — simply stops being pending, with no separate
 * bookkeeping to keep in step.
 *
 * Returns the gender alongside the ticket because the two are inseparable: the
 * reel spins one gender's pool at a time, so awarding a merit winner also
 * decides which track that spin runs on.
 *
 * @param meritWinners string[] ticket codes configured for this phase
 * @param eligiblePool {{M: Array<{lottery: string}>, F: Array<{lottery: string}>}}
 * @returns {{lottery: string, gender: 'M' | 'F'} | null} null for a draw by chance
 */
export const pickMeritWinner = ({
                                    meritWinners,
                                    eligiblePool,
                                    maleWinners,
                                    femaleWinners,
                                    maxMaleWinners,
                                    maxFemaleWinners,
                                }) => {
    if (!meritWinners?.length) return null;

    const drawn = maleWinners + femaleWinners;
    if (drawn < meritThreshold(maxMaleWinners + maxFemaleWinners)) return null;

    // A gender whose quota is full cannot take another winner, on merit or
    // otherwise — awarding one there would hand out a prize the phase never had.
    const hasRoom = {
        M: maleWinners < maxMaleWinners,
        F: femaleWinners < maxFemaleWinners,
    };

    // Configured order, so several merit winners come out as they are listed.
    for (const lottery of meritWinners) {
        for (const gender of ['M', 'F']) {
            if (!hasRoom[gender]) continue;
            if (eligiblePool[gender].some(item => item.lottery === lottery)) {
                return {lottery, gender};
            }
        }
    }

    return null;
};
